// @owners { team: patients-team }
import { getScheduleablePrescriptions } from '~shared/features/my-meds/selectors/getScheduleablePrescriptions';
import { getNextAvailableDateID } from '~shared/features/next-available-date/helpers/getNextAvailableDateID';
import { getNextAvailableDateFacilityID } from '~shared/features/next-available-date/selectors/getNextAvailableDateFacilityId';
import { getNextAvailableDates } from '~shared/features/next-available-date/selectors/getNextAvailableDates';
import getShipmentID from '~shared/features/next-available-date/selectors/getShipmentId';
import { getPrescriptionsForShipment } from '~shared/features/shipments/selectors/getPrescriptionsForShipment';
import { get } from '~shared/helpers/apiHelper';
import { type ReduxDispatch, type ReduxGetStateShared, type ReduxStateShared } from '~shared/types';
import { type APIError } from '~shared/types/APIError';
import { type NextAvailableDateResponse } from '~shared/types/clients';

export const FETCH_NEXT_AVAILABLE_DATES_LOADING = 'FETCH_NEXT_AVAILABLE_DATES_LOADING';

type ActionFetchNextAvailableDatesLoading = {
  type: typeof FETCH_NEXT_AVAILABLE_DATES_LOADING;
};

/** Dispatched before a NAD network request (used by React Query migration helper). */
export const fetchingNextAvailableDates = (): ActionFetchNextAvailableDatesLoading => {
  return {
    type: FETCH_NEXT_AVAILABLE_DATES_LOADING,
  };
};

export const FETCH_NEXT_AVAILABLE_DATES_FAILED = 'FETCH_NEXT_AVAILABLE_DATES_FAILED';

export type ActionFetchNextAvailableDatesFailed = {
  type: typeof FETCH_NEXT_AVAILABLE_DATES_FAILED;
  payload: APIError;
  error: true;
};

export function fetchNextAvailableDatesFailed(error: APIError): ActionFetchNextAvailableDatesFailed {
  return {
    type: FETCH_NEXT_AVAILABLE_DATES_FAILED,
    payload: error,
    error: true,
  };
}

export const FETCH_NEXT_AVAILABLE_DATES_SUCCEEDED = 'FETCH_NEXT_AVAILABLE_DATES_SUCCEEDED';

export type ActionFetchNextAvailableDatesSucceeded = {
  type: typeof FETCH_NEXT_AVAILABLE_DATES_SUCCEEDED;
  payload: NextAvailableDateResponse[];
};

export function fetchNextAvailableDatesSucceeded(
  nextAvailableDates: NextAvailableDateResponse[],
): ActionFetchNextAvailableDatesSucceeded {
  return {
    type: FETCH_NEXT_AVAILABLE_DATES_SUCCEEDED,
    payload: nextAvailableDates,
  };
}

export type NextAvailableDateActions =
  | ActionFetchNextAvailableDatesFailed
  | ActionFetchNextAvailableDatesSucceeded
  | ActionFetchNextAvailableDatesLoading;

type FetchNextAvailableDatesRequest = {
  address_id?: number;
  facility_id?: number;
  shipment_id?: number;
  unbundled?: string;
  bundled?: string;
};

export type FetchNextAvailableDatesOptions = {
  unbundledPrescriptionIDs?: number[];
  bundledPrescriptionIDs?: number[];
  addressID: number | undefined;
};

/** NAD cache TTL; also used by {@link getNextAvailableDateFreshnessByIDMap}. */
export const NEXT_AVAILABLE_DATES_REFETCH_THRESHOLD_MS = 1000 * 60 * 5; // 5 minutes

/**
 * NAD IDs the fetch thunk uses for its cache check (unbundled per-Rx, optional bundled set, optional shipment bundle).
 */
export const collectNextAvailableDateRequestIds = (
  state: ReduxStateShared,
  options: FetchNextAvailableDatesOptions,
): string[] => {
  const addressID = options.addressID;
  const facilityID = getNextAvailableDateFacilityID(state);
  const unbundled =
    options.unbundledPrescriptionIDs ||
    getScheduleablePrescriptions(state, { nextAvailableDateAddressID: addressID }).map((rx) => rx.id);
  const bundled = options.bundledPrescriptionIDs || [];
  const shipmentID = getShipmentID(state);

  const nextAvailableDateIDs = unbundled.map((prescriptionID) =>
    getNextAvailableDateID({
      addressID,
      facilityID,
      prescriptionIDs: [prescriptionID],
    }),
  );
  if (bundled.length > 0) {
    nextAvailableDateIDs.push(
      getNextAvailableDateID({
        addressID,
        facilityID,
        prescriptionIDs: bundled,
      }),
    );
  }
  if (shipmentID) {
    const shipmentPrescriptionIDs = getPrescriptionsForShipment(state, { shipmentID }).map((rx) => rx.id);
    nextAvailableDateIDs.push(
      getNextAvailableDateID({
        addressID,
        facilityID,
        prescriptionIDs: shipmentPrescriptionIDs,
      }),
    );
  }
  return nextAvailableDateIDs;
};

/**
 * True when Redux already has fresh NAD entries for every ID the fetch would consult (no network required).
 */
export const isNextAvailableDatesFetchSatisfiedByCache = (
  state: ReduxStateShared,
  options: FetchNextAvailableDatesOptions,
  nowMs: number = Date.now(),
): boolean => {
  const addressID = options.addressID;
  const facilityID = getNextAvailableDateFacilityID(state);
  if (!addressID && !facilityID) {
    return false;
  }
  const nextAvailableDates = getNextAvailableDates(state);
  const ids = collectNextAvailableDateRequestIds(state, options);
  return ids.every(
    (nextAvailableDateID) =>
      nextAvailableDates[nextAvailableDateID]?.computedAt > nowMs - NEXT_AVAILABLE_DATES_REFETCH_THRESHOLD_MS,
  );
};

/**
 * @deprecated Redux networking action.
 * @see https://www.notion.so/alto/Guidebook-Migrating-Redux-Networking-Actions-To-React-Query-8567e05fc96c46fcb8020595f24b0edc
 */
export const fetchNextAvailableDates = (options: FetchNextAvailableDatesOptions) => {
  return (dispatch: ReduxDispatch, getState: ReduxGetStateShared) => {
    const state = getState();

    const addressID = options.addressID;
    const facilityID = getNextAvailableDateFacilityID(state);
    const unbundled =
      options.unbundledPrescriptionIDs ||
      getScheduleablePrescriptions(state, { nextAvailableDateAddressID: addressID }).map((rx) => rx.id);
    const bundled = options.bundledPrescriptionIDs || [];
    const shipmentID = getShipmentID(state);

    // Fail early if neither address or facility is available.
    // One of either is required for calculating the next available date
    if (!addressID && !facilityID) {
      dispatch(
        fetchNextAvailableDatesFailed({
          details: {
            message: 'One of address or facility is required.',
            statusCode: 400,
            statusType: 'error',
          },
          message: 'One of address or facility is required.',
          name: 'error',
        }),
      );

      return Promise.resolve(false);
    }

    // Exit early if we've already fetched these next available dates within the last 5 minutes. Ultimately, we
    // should replace this networking action with a react-query hook, which will provide this caching automatically.
    if (isNextAvailableDatesFetchSatisfiedByCache(state, options)) {
      dispatch(fetchNextAvailableDatesSucceeded([]));
      return Promise.resolve(true);
    }

    // 2023-10-20 alau: this is a temporary fix. we're seeing tons of attempts for empty NAD requests getting through
    // here, but don't know exactly where they are coming from.
    if (unbundled.length === 0 && bundled.length === 0 && !shipmentID) {
      dispatch(fetchNextAvailableDatesSucceeded([]));

      return Promise.resolve(true);
    }

    dispatch(fetchingNextAvailableDates());

    const params: FetchNextAvailableDatesRequest = {};

    if (addressID) {
      params.address_id = addressID;
    } else {
      params.facility_id = facilityID;
    }

    if (unbundled.length > 0) {
      params.unbundled = unbundled.join(',');
    }

    if (bundled.length > 0) {
      params.bundled = bundled.join(',');
    }

    if (shipmentID) {
      params.shipment_id = shipmentID;
    }

    return get('/next_available_dates', params, { version: 'v3' }).then((json) => {
      if (json.error) {
        dispatch(fetchNextAvailableDatesFailed(json.error));

        return false;
      }

      dispatch(fetchNextAvailableDatesSucceeded(json));

      return true;
    });
  };
};
